import type { CanvasTextAlign } from '@/src/renderer/lib/db/models'
import type { CanvasNoteColorKey } from '../utils/canvasNoteStyle'
import type { CanvasTextHighlightKey } from '../utils/canvasText'
import type { CanvasTextFormatChange } from '../utils/canvasTextFormat'

/**
 * Ein Knopf der **Options-Leiste**, in der Reihenfolge des Figma-Entwurfs.
 * `marks` sind die vier Umschalter Fett, Kursiv, Unterstrichen, Durchgestrichen
 * — ein Eintrag, weil zwischen ihnen kein Trenner steht.
 */
export type CanvasOptionsBarButton =
  | 'emoji' | 'color' | 'font' | 'size' | 'marks' | 'highlight' | 'list' | 'align'
  | 'title' | 'author' | 'apply' | 'tape' | 'delete' | 'retape' | 'untape'

/** Was die Formatier-Knöpfe eines **Texts** zeigen (`marks`, `highlight`, `list`, `align`). */
export interface CanvasOptionsBarTextFormat {
  bold: boolean
  italic: boolean
  underline: boolean
  strike: boolean
  /** Die Marker-Farbe; `null` ohne, `undefined` gemischt. */
  highlight: CanvasTextHighlightKey | null | undefined
  list: boolean
  align: CanvasTextAlign
  /** Ob der Text überhaupt einen Marker trägt — sonst gibt es nichts neu zu mischen. */
  hasMarkers?: boolean
}

/**
 * Was die Farbe eines Elements färbt: die Fläche eines Zettels (`surface`) oder
 * die Schrift eines **Texts** (`ink`). Bei `ink` ist `null` die
 * **Standard**-Tinte — ein eigener Wert, keine Vorgabefarbe, und der Wähler
 * bietet sie als ersten Tropfen an.
 */
export type CanvasOptionsBarColorKind = 'surface' | 'ink'

/**
 * Wohin die Leiste ihre Aufkläpper öffnet.
 *
 * `above` ist der Normalfall — die Leiste schwebt über dem Zettel, ihre
 * Aufkläpper gehen darüber auf. Steht die Leiste selbst unter dem Zettel, weil
 * oben kein Platz war, gehen auch sie nach unten: Sonst stünde die Leiste unten
 * und ihr Menü oben, mit dem Zettel dazwischen.
 */
export type CanvasOptionsBarPlacement = 'above' | 'below'

/**
 * Ob der **Übernehmen-Knopf** etwas zu tun hat: `ready` — mindestens ein
 * Element lässt sich übernehmen; `done` — alles schon übernommen; `empty` —
 * kein Text, also nichts, was eine Aufgabe werden könnte. Nur `ready` ist
 * bedienbar; die anderen beiden sind **tot statt folgenlos** (CONTEXT.md).
 */
export type CanvasOptionsBarApplyState = 'ready' | 'done' | 'empty'

/**
 * Abstand der **Options-Leiste** zum Zettel, in Bildschirm-Pixeln (React
 * Flows `NodeToolbar` skaliert nicht mit). 40 statt der Vorgabe (10): Der
 * obere **Anlegepunkt** steht 12px über der Kante und wächst gehoben auf
 * 20px — die Leiste muss darüber frei bleiben (Figma `4873:110556`).
 */
export const OPTIONS_BAR_OFFSET = 40

/**
 * Das Signal an die Leiste, dass ihre Hülle (`.react-flow__node-toolbar`) von
 * außen verschoben wurde — sie misst dann ihr Ausweichen neu. Der **Text**
 * schiebt sie mit, solange seine neue Lage noch nicht geschrieben ist
 * (`useMeasuredTextSize`); gemessen hätte sie sonst eine Lage, die es einen
 * Augenblick später nicht mehr gibt.
 */
export const OPTIONS_BAR_REPOSITION_EVENT = 'canvas-options-bar:reposition'

/**
 * Was der **Farbwähler** beim Überfahren meldet (CONTEXT.md, Farbwähler):
 * - `{ color }` — das Element zeigt diese Farbe schon; `color: null` ist der
 *   vordere Eintrag (Standard-Tinte bzw. kein Marker).
 * - `null` — die Vorschau ist aus, es gilt wieder die eingestellte Farbe.
 * - `'picked'` — die Vorschau ist aus, weil geklickt wurde. Sie bleibt noch
 *   stehen, bis die geschriebene Farbe zurück ist — sonst blitzte für einen
 *   Frame die alte auf (siehe `useOptionPreview`).
 */
export type CanvasColorPreview = CanvasOptionPreview<{ color: string | null }>

/** Wie `CanvasColorPreview`, für das **Schriftmenü**; `font: null` ist die Standardschrift. */
export type CanvasFontPreview = CanvasOptionPreview<{ font: string | null }>

/** Die drei Meldungen einer Vorschau — siehe `CanvasColorPreview`. */
export type CanvasOptionPreview<T> = T | null | 'picked'

export interface CanvasOptionsBarProps {
  /** Welche Knöpfe, in dieser Reihenfolge. */
  buttons: readonly CanvasOptionsBarButton[]
  /**
   * Die Farben der gemeinten Elemente — ein Eintrag je Element.
   *
   * Daraus entsteht beides: der **Ton der Leiste** (bei genau einer Farbe die
   * ihre, sonst neutral) und der **Farb-Tropfen** (bei mehreren verschiedenen
   * diagonal geteilt).
   */
  colorKeys: readonly (string | null)[]
  /** Die Schriften der gemeinten Elemente — ein Eintrag je Element. */
  fontKeys: readonly (string | null)[]
  /** Ob die Farbe die Fläche oder die Schrift meint; ohne Angabe `surface`. */
  colorKind?: CanvasOptionsBarColorKind
  /** Die Schriftgrößen der gemeinten **Texte** — für den Knopf `'size'`. */
  fontSizes?: readonly number[]
  /** Ob der Titel-Umschalter eingeschaltet aussieht (nur wenn ALLE ihn zeigen). */
  titleOn?: boolean
  /** Ob der Autor-Umschalter eingeschaltet aussieht (nur wenn ALLE ihn zeigen). */
  authorOn?: boolean
  placement?: CanvasOptionsBarPlacement
  onColorChange: (key: CanvasNoteColorKey) => void
  onFontChange: (key: string | null) => void
  /** Eine Größe aus dem **Größen-Menü** (Knopf `'size'`). */
  onFontSizeChange?: (size: number) => void
  /** Die **Standard**-Tinte gewählt (nur bei `colorKind: 'ink'`). */
  onColorReset?: () => void
  /**
   * Die Vorschau der Farbe beim Überfahren der Tropfen. Schreibt nie — das
   * tut allein der Klick über `onColorChange` / `onColorReset`.
   */
  onColorPreview?: (preview: CanvasColorPreview) => void
  /** Wie `onColorPreview`, für den **Marker** (Knopf `'highlight'`). */
  onHighlightPreview?: (preview: CanvasColorPreview) => void
  /** Wie `onColorPreview`, für das **Schriftmenü** (Knopf `'font'`). */
  onFontPreview?: (preview: CanvasFontPreview) => void
  /** Der Stand der Formatier-Knöpfe; ohne ihn zeigen sie nichts als an. */
  textFormat?: CanvasOptionsBarTextFormat
  /** Eine Wahl aus den Formatier-Knöpfen — wo sie wirkt, entscheidet der Text. */
  onTextFormat?: (change: CanvasTextFormatChange) => void
  onToggleTitle?: () => void
  onToggleAuthor?: () => void
  onDelete: () => void
  /**
   * **Ankleben** (`buttons` enthält `'tape'`, links vom Löschen): Ein
   * **Klebestreifen** kommt auf die gemeinten Zettel. Fehlt der Rückruf, wird
   * der Knopf nicht gerendert — wie beim Übernehmen.
   */
  onTape?: () => void
  /**
   * **Streifen wechseln** (`buttons` enthält `'retape'`, rechts vom Ablösen —
   * ganz rechts wie **Marker wechseln** im Marker-Wähler):
   * ein anderes Stück aus der Schachtel, nie dasselbe wie das aktuelle.
   */
  onRetape?: () => void
  /**
   * **Ablösen** (`buttons` ist dann `['untape', 'retape']` und sonst nichts):
   * Der Streifen geht ab, der Zettel ist wieder frei.
   */
  onUntape?: () => void
  /**
   * Der **Übernehmen-Knopf** in der Leiste (`buttons` enthält `'apply'`,
   * links vom Löschen). Fehlt der Rückruf, wird der Knopf nicht gerendert
   * (Story, Test, ein Aufrufer ohne Übernahme).
   */
  onApply?: () => void
  /** Zustand des Knopfs; ohne Angabe `ready`. */
  applyState?: CanvasOptionsBarApplyState
  /** Wie viele Elemente der Knopf übernimmt — für den Titel der Gruppen-Leiste. */
  applyCount?: number
  /**
   * Nach JEDER Aktion (Farbe, Schrift, Titel, Autor, Löschen) — nicht schon
   * beim Aufklappen eines Menüs. Der Aufrufer holt sich damit den Fokus
   * zurück: Nach einem Klick in der Leiste läge er sonst auf ihrem Knopf oder,
   * wenn ein Menü gerade zugeht, auf `document.body`, und die Kürzel am
   * Canvas (Entf, ⌘D …) kämen nie an.
   */
  onActionDone?: () => void
  /**
   * Die Leiste blendet gerade ab (siehe `usePresence`). Als `data-leaving`
   * ans Wurzelelement — die SCSS spielt daran rückwärts, was sie beim
   * Erscheinen vorwärts spielt; Klicks nimmt sie dabei keine mehr an.
   */
  leaving?: boolean
  className?: string
}
